/**
 * The router is generated from the screen catalog.
 *
 * Nothing is hand-listed here, so "every screen in spec section 7 has a
 * reachable placeholder route" is true by construction rather than by review, and
 * `router.spec.ts` checks it by resolving every catalog path.
 */

import { createRouter, createWebHistory, type RouteRecordRaw, type Router, type RouterHistory } from 'vue-router'

import { SCREENS, type ScreenDefinition } from '@/screens/catalog'
import type { ScreenId } from '@/screens/interaction-states'

declare module 'vue-router' {
  interface RouteMeta {
    screenId: ScreenId
    /**
     * Chooses the layout. FND-11's two — authenticated and not — plus sign-in,
     * which is signed out but has its own design.
     */
    layout: 'authenticated' | 'public' | 'sign-in'
    title: string
  }
}

function toRoute (screen: ScreenDefinition): RouteRecordRaw {
  return {
    path: screen.path,
    name: screen.name,
    component: screen.component,
    meta: {
      screenId: screen.id,
      layout: screen.layout ?? (screen.access.kind === 'role' ? 'authenticated' : 'public'),
      title: screen.title
    }
  }
}

/**
 * `history` is injectable so the router tests can use an in-memory history and
 * still exercise the real route table and the real guards. A test that built its
 * own routes would be testing itself.
 */
export function createAppRouter (history?: RouterHistory): Router {
  return createRouter({
    // FND-06 configures the SPA 404-to-index fallback, so history mode is the
    // right choice and hash routing would be a workaround for a solved problem.
    history: history ?? createWebHistory(),
    routes: SCREENS.map(toRoute),
    scrollBehavior (to, from, saved) {
      if (saved) return saved
      // Preserve position when only the query changed — paging a table or
      // toggling a filter should not throw the user back to the top.
      if (to.path === from.path) return false
      return { top: 0 }
    }
  })
}
